import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
    PencilAltIcon,
    MapMarkerIcon,
    CubesIcon,
    MonitoringIcon,
    PlusCircleIcon,
    EllipsisVIcon,
    TrashIcon,
    TimesIcon
} from '@patternfly/react-icons';
import { useNavigate } from 'react-router-dom';
import Markdown from 'react-markdown';
import toast from 'react-hot-toast';
import { updateOrganization, triggerBusinessResearch, toggleBusinessSummary, updateBusinessSummaryText } from '@app/utils/utils';

export interface IOrganizationHeroCardProps {
    organization: {
        id: string;
        organizationName: string;
        category?: string;
        address?: {
            street?: string;
            moreInfo?: string;
            city?: string;
            state?: string;
            zipcode?: string;
            country?: string;
        };
        aboutBusiness?: string;
        businessSummary?: string;
        useBusinessSummary?: boolean;
        summaryModifiedByUser?: boolean;
        summaryModifiedAt?: string;
        researchStatus?: 'NONE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
    };
    adsCount?: number;
    campaignsCount?: number;
    onEditClick?: (organization: any) => void;
    onDeleteClick?: (organization: any) => void;
    onResearchTriggered?: () => void;
    // Onboarding tour: spotlight the "Research My Business" button and advance
    // the tour (onResearchTourClick) once the user kicks off research.
    highlightResearch?: boolean;
    onResearchTourClick?: () => void;
    onToggleBusinessSummary?: () => void;
    onCollapse?: () => void;
}

const OrganizationHeroCard: React.FunctionComponent<IOrganizationHeroCardProps> = ({
    organization,
    adsCount = 0,
    campaignsCount = 0,
    onEditClick,
    onDeleteClick,
    onResearchTriggered,
    highlightResearch = false,
    onResearchTourClick,
    onToggleBusinessSummary,
    onCollapse
}) => {
    const navigate = useNavigate();
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // About My Business state
    const [aboutText, setAboutText] = useState(organization.aboutBusiness || '');
    const [isSaving, setIsSaving] = useState(false);
    const isResearching = organization.researchStatus === 'IN_PROGRESS';

    // Business summary toggle state
    const [showSummaryToggleConfirm, setShowSummaryToggleConfirm] = useState(false);
    const [isTogglingBs, setIsTogglingBs] = useState(false);
    const isSummaryEnabled = organization.useBusinessSummary !== false;

    // Business summary edit state
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [summaryText, setSummaryText] = useState(organization.businessSummary || '');
    const [isSavingSummary, setIsSavingSummary] = useState(false);

    // Sync aboutText when organization changes
    useEffect(() => {
        setAboutText(organization.aboutBusiness || '');
    }, [organization.aboutBusiness]);

    // Sync summaryText when organization changes
    useEffect(() => {
        setSummaryText(organization.businessSummary || '');
    }, [organization.businessSummary]);

    const handleSaveAbout = useCallback(async () => {
        setIsSaving(true);
        try {
            await updateOrganization(organization.id, {
                organizationName: organization.organizationName,
                address: organization.address,
                category: organization.category,
                aboutBusiness: aboutText
            });
        } catch (error) {
            console.error('Failed to save about business:', error);
        } finally {
            setIsSaving(false);
        }
    }, [aboutText, organization]);

    const handleTriggerResearch = useCallback(async () => {
        try {
            await triggerBusinessResearch(organization.id);
            if (onResearchTriggered) {
                onResearchTriggered();
            }
            // If the onboarding tour is spotlighting this button, advance it to
            // the Plan-tab step now that research is underway.
            if (highlightResearch && onResearchTourClick) {
                onResearchTourClick();
            }
        } catch (error) {
            console.error('Failed to trigger business research:', error);
        }
    }, [organization.id, onResearchTriggered, highlightResearch, onResearchTourClick]);

    const handleConfirmToggleSummary = useCallback(async () => {
        setIsTogglingBs(true);
        try {
            await toggleBusinessSummary(organization.id, !isSummaryEnabled);
            toast(isSummaryEnabled
                ? 'Business summary disabled for ad generation'
                : 'Business summary enabled for ad generation');
            if (onToggleBusinessSummary) {
                onToggleBusinessSummary();
            }
        } catch (error) {
            console.error('Failed to toggle business summary:', error);
        } finally {
            setIsTogglingBs(false);
            setShowSummaryToggleConfirm(false);
        }
    }, [organization.id, isSummaryEnabled, onToggleBusinessSummary]);

    const handleSaveSummary = useCallback(async () => {
        setIsSavingSummary(true);
        try {
            await updateBusinessSummaryText(organization.id, summaryText);
            toast('Business summary updated');
            setIsEditingSummary(false);
            if (onResearchTriggered) {
                // Reuse existing refresh callback to refresh org data
                onResearchTriggered();
            }
        } catch (error) {
            console.error('Failed to save business summary:', error);
        } finally {
            setIsSavingSummary(false);
        }
    }, [organization.id, summaryText, onResearchTriggered]);

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function() {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Generate gradient based on organization name
    const getGradientFromName = (name: string) => {
        const gradients = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
    };

    const gradient = getGradientFromName(organization.organizationName || 'Org');
    const initial = (organization.organizationName || 'O').charAt(0).toUpperCase();

    const formatAddress = () => {
        if (!organization.address) return null;
        const addr = organization.address;
        const parts: string[] = [];
        if (addr.street) parts.push(addr.street);
        if (addr.moreInfo) parts.push(addr.moreInfo);
        const cityLine = [addr.city, addr.state, addr.zipcode].filter(Boolean).join(', ');
        if (cityLine) parts.push(cityLine);
        if (addr.country) parts.push(addr.country);
        return parts;
    };

    const addressLines = formatAddress();

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        if (onEditClick) {
            onEditClick(organization);
        }
    };

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
        if (onDeleteClick) {
            onDeleteClick(organization);
        }
    };

    const handleCancelDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    const styles = {
        card: {
            background: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'row' as const,
            border: onCollapse ? '2px solid #667eea' : '2px solid transparent'
        },
        collapseButton: {
            background: 'transparent',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ef4444',
            transition: 'all 0.2s',
            marginLeft: '8px'
        },
        leftColumn: {
            flex: '1',
            padding: '32px',
            display: 'flex',
            flexDirection: 'column' as const,
            borderRight: '1px solid #e5e7eb'
        },
        rightColumn: {
            width: '280px',
            padding: '32px',
            background: '#f8fafc',
            display: 'flex',
            flexDirection: 'column' as const
        },
        orgHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '24px'
        },
        initialCircle: {
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            fontWeight: 800,
            color: '#ffffff',
            flexShrink: 0
        },
        orgInfo: {
            flex: 1
        },
        orgName: {
            color: '#1f2937',
            fontSize: '24px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '4px'
        },
        category: {
            color: '#6b7280',
            fontSize: '14px',
            fontWeight: 500,
            margin: 0,
            textTransform: 'capitalize' as const
        },
        menuContainer: {
            position: 'relative' as const
        },
        menuButton: {
            background: 'transparent',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            transition: 'all 0.2s'
        },
        dropdownMenu: {
            position: 'absolute' as const,
            top: '100%',
            right: 0,
            marginTop: '4px',
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            zIndex: 100,
            minWidth: '140px'
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const
        },
        menuItemDanger: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#dc2626',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const
        },
        addressSection: {
            flex: 1
        },
        addressRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
        },
        addressIcon: {
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            flexShrink: 0
        },
        addressText: {
            color: '#374151',
            fontSize: '14px',
            lineHeight: 1.7
        },
        sectionTitle: {
            fontSize: '11px',
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
            marginBottom: '16px'
        },
        statsContainer: {
            display: 'flex',
            gap: '12px',
            marginBottom: '24px'
        },
        statCard: {
            flex: 1,
            background: '#ffffff',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center' as const,
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: '1px solid #e5e7eb'
        },
        statNumber: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937',
            margin: 0
        },
        statLabel: {
            fontSize: '12px',
            color: '#6b7280',
            margin: 0,
            marginTop: '4px'
        },
        actionsContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '10px',
            marginTop: '0'
        },
        actionButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: 'none',
            width: '100%'
        },
        primaryAction: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.3)'
        },
        secondaryAction: {
            background: '#ffffff',
            color: '#667eea',
            border: '2px solid #667eea'
        },
        // Delete confirmation modal styles
        confirmOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        confirmModal: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        confirmTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0'
        },
        confirmText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.5
        },
        confirmButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        cancelButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            background: '#ffffff',
            color: '#374151',
            cursor: 'pointer'
        },
        deleteButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: '#dc2626',
            color: '#ffffff',
            cursor: 'pointer'
        },
        primaryConfirmButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            cursor: 'pointer'
        },
        // About My Business styles
        aboutSection: {
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #e5e7eb'
        },
        aboutHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
        },
        aboutTitle: {
            fontSize: '13px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px'
        },
        aboutInfoIcon: {
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#6b7280',
            cursor: 'help',
            fontWeight: 700
        },
        aboutTextarea: {
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            outline: 'none',
            resize: 'vertical' as const,
            fontFamily: 'inherit',
            lineHeight: 1.5,
            color: '#374151',
            backgroundColor: '#f9fafb',
            transition: 'border-color 0.2s, background-color 0.2s',
            boxSizing: 'border-box' as const
        },
        aboutActions: {
            display: 'flex',
            gap: '8px',
            marginTop: '10px'
        },
        saveButton: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '6px',
            background: '#667eea',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
        },
        researchButton: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: '2px solid #667eea',
            borderRadius: '6px',
            background: '#ffffff',
            color: '#667eea',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'opacity 0.2s'
        },
        researchButtonDisabled: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: '2px solid #d1d5db',
            borderRadius: '6px',
            background: '#f3f4f6',
            color: '#9ca3af',
            cursor: 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
        },
        researchTourWrapper: {
            position: 'relative' as const,
            display: 'inline-block'
        },
        researchTourSpotlight: {
            position: 'absolute' as const,
            top: '-10px',
            left: '-10px',
            right: '-10px',
            bottom: '-10px',
            borderRadius: '12px',
            border: '3px solid #fbbf24',
            boxShadow: '0 0 0 4px rgba(251, 191, 36, 0.3), 0 0 30px rgba(251, 191, 36, 0.4)',
            pointerEvents: 'none' as const,
            animation: 'researchTourPulse 2s ease-in-out infinite'
        },
        researchTourArrow: {
            position: 'absolute' as const,
            left: '100%',
            top: '50%',
            marginLeft: '14px',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'row' as const,
            alignItems: 'center',
            animation: 'researchTourBounce 1s ease-in-out infinite',
            pointerEvents: 'none' as const
        },
        researchTourArrowIcon: {
            width: 0,
            height: 0,
            borderTop: '9px solid transparent',
            borderBottom: '9px solid transparent',
            borderRight: '11px solid #fbbf24'
        },
        researchTourLabel: {
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: '#1f2937',
            padding: '7px 14px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 700,
            whiteSpace: 'nowrap' as const,
            boxShadow: '0 4px 12px rgba(251, 191, 36, 0.4)',
            marginLeft: '8px'
        },
        summarySection: {
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
        },
        summaryHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px'
        },
        summaryTitle: {
            fontSize: '13px',
            fontWeight: 600,
            color: '#667eea',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px'
        },
        summaryToggleRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px'
        },
        summaryToggleLabel: {
            fontSize: '13px',
            fontWeight: 500,
            color: '#374151'
        },
        toggleSwitch: {
            position: 'relative' as const,
            width: '44px',
            height: '24px',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            padding: 0,
            flexShrink: 0
        },
        toggleKnob: {
            position: 'absolute' as const,
            top: '2px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: '#ffffff',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        },
        summaryCard: {
            background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid #e0e7ff'
        },
        summaryText: {
            fontSize: '14px',
            lineHeight: 1.7,
            color: '#374151',
        },
        summaryEditButton: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#667eea',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: 600,
            padding: '2px 6px',
            borderRadius: '4px',
            transition: 'background 0.2s'
        },
        summaryTextarea: {
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            border: '1px solid #c7d2fe',
            borderRadius: '8px',
            outline: 'none',
            resize: 'vertical' as const,
            fontFamily: 'inherit',
            lineHeight: 1.7,
            color: '#374151',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box' as const,
            minHeight: '150px'
        },
        summaryModifiedBadge: {
            fontSize: '11px',
            fontWeight: 600,
            color: '#667eea',
            backgroundColor: '#eef2ff',
            padding: '2px 8px',
            borderRadius: '10px'
        },
        summaryActions: {
            display: 'flex',
            gap: '8px',
            marginTop: '10px'
        },
        spinner: {
            display: 'inline-block',
            width: '14px',
            height: '14px',
            border: '2px solid #d1d5db',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'heroCardSpin 0.8s linear infinite'
        }
    };

    return (
        <div className="org-hero-card" style={styles.card}>
            {/* Left Column - Organization Info */}
            <div className="org-hero-left" style={styles.leftColumn}>
                <div className="org-hero-org-header" style={styles.orgHeader}>
                    <div className="org-hero-initial" style={styles.initialCircle}>
                        {initial}
                    </div>
                    <div style={styles.orgInfo}>
                        <h1 className="org-hero-name" style={styles.orgName}>{organization.organizationName}</h1>
                        <p style={styles.category}>{organization.category || 'Business'}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={styles.menuContainer} ref={menuRef}>
                            <button
                                style={styles.menuButton}
                                onClick={handleMenuClick}
                                type="button"
                                title="More options"
                            >
                                <EllipsisVIcon />
                            </button>
                            {showMenu && (
                                <div style={styles.dropdownMenu}>
                                    <button
                                        style={styles.menuItem}
                                        onClick={handleEditClick}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                    >
                                        <PencilAltIcon /> Edit
                                    </button>
                                    <button
                                        style={styles.menuItemDanger}
                                        onClick={handleDeleteClick}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                    >
                                        <TrashIcon /> Delete
                                    </button>
                                </div>
                            )}
                        </div>
                        {onCollapse && (
                            <button
                                style={styles.collapseButton}
                                onClick={onCollapse}
                                type="button"
                                title="Collapse"
                            >
                                <TimesIcon />
                            </button>
                        )}
                    </div>
                </div>

                {/* Address */}
                {addressLines && addressLines.length > 0 && (
                    <div style={styles.addressSection}>
                        <div style={styles.addressRow}>
                            <div style={styles.addressIcon}>
                                <MapMarkerIcon />
                            </div>
                            <div style={styles.addressText}>
                                {addressLines.map((line, index) => (
                                    <div key={index}>{line}</div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* About My Business */}
                <div style={styles.aboutSection}>
                    <div style={styles.aboutHeader}>
                        <span style={styles.aboutTitle}>About My Business</span>
                        <span style={styles.aboutInfoIcon} title="Describe your business to help generate better ads">?</span>
                    </div>
                    <textarea
                        style={styles.aboutTextarea}
                        rows={3}
                        placeholder="Describe what your business does, your target customers, and what makes you unique..."
                        value={aboutText}
                        onChange={(e) => setAboutText(e.target.value)}
                        onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.backgroundColor = '#ffffff'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.backgroundColor = '#f9fafb'; }}
                    />
                    <div style={styles.aboutActions}>
                        <button
                            style={{ ...styles.saveButton, opacity: isSaving ? 0.7 : 1 }}
                            onClick={handleSaveAbout}
                            disabled={isSaving}
                            type="button"
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        {highlightResearch ? (
                            <div style={styles.researchTourWrapper}>
                                <div style={styles.researchTourSpotlight} />
                                <button
                                    style={isResearching || !aboutText ? styles.researchButtonDisabled : styles.researchButton}
                                    onClick={handleTriggerResearch}
                                    disabled={isResearching || !aboutText}
                                    type="button"
                                >
                                    {isResearching ? (
                                        <>
                                            <span style={styles.spinner} />
                                            Researching...
                                        </>
                                    ) : (
                                        <>&#10024; Research My Business</>
                                    )}
                                </button>
                                <div style={styles.researchTourArrow}>
                                    <div style={styles.researchTourArrowIcon} />
                                    <div style={styles.researchTourLabel}>
                                        {aboutText ? 'Next: research your business!' : 'Add a description above, then research!'}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <button
                                style={isResearching || !aboutText ? styles.researchButtonDisabled : styles.researchButton}
                                onClick={handleTriggerResearch}
                                disabled={isResearching || !aboutText}
                                type="button"
                            >
                                {isResearching ? (
                                    <>
                                        <span style={styles.spinner} />
                                        Researching...
                                    </>
                                ) : (
                                    <>&#10024; Research My Business</>
                                )}
                            </button>
                        )}
                    </div>

                    {/* AdVolt Business Summary */}
                    {organization.businessSummary && (
                        <div style={styles.summarySection}>
                            <div style={styles.summaryHeader}>
                                <span style={styles.summaryTitle}>AdVolt Business Summary</span>
                                {!isEditingSummary && (
                                    <button
                                        style={styles.summaryEditButton}
                                        onClick={() => setIsEditingSummary(true)}
                                        type="button"
                                        title="Edit business summary"
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                    >
                                        <PencilAltIcon /> Edit
                                    </button>
                                )}
                                {organization.summaryModifiedByUser && !isEditingSummary && (
                                    <span style={styles.summaryModifiedBadge}>Edited by you</span>
                                )}
                            </div>
                            <div style={styles.summaryToggleRow}>
                                <span style={styles.summaryToggleLabel}>Use in Ad Generation</span>
                                <button
                                    style={{
                                        ...styles.toggleSwitch,
                                        background: isSummaryEnabled ? '#667eea' : '#d1d5db'
                                    }}
                                    onClick={() => setShowSummaryToggleConfirm(true)}
                                    type="button"
                                    title={isSummaryEnabled ? 'Disable business summary for ad generation' : 'Enable business summary for ad generation'}
                                >
                                    <div style={{
                                        ...styles.toggleKnob,
                                        left: isSummaryEnabled ? '22px' : '2px'
                                    }} />
                                </button>
                            </div>
                            {isEditingSummary ? (
                                <div>
                                    <textarea
                                        style={styles.summaryTextarea}
                                        value={summaryText}
                                        onChange={(e) => setSummaryText(e.target.value)}
                                        rows={8}
                                    />
                                    <div style={styles.summaryActions}>
                                        <button
                                            style={{ ...styles.saveButton, opacity: isSavingSummary ? 0.7 : 1 }}
                                            onClick={handleSaveSummary}
                                            disabled={isSavingSummary}
                                            type="button"
                                        >
                                            {isSavingSummary ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            style={styles.cancelButton}
                                            onClick={() => {
                                                setIsEditingSummary(false);
                                                setSummaryText(organization.businessSummary || '');
                                            }}
                                            type="button"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    ...styles.summaryCard,
                                    opacity: isSummaryEnabled ? 1 : 0.5,
                                    transition: 'opacity 0.3s'
                                }}>
                                    <div className="business-summary-markdown" style={styles.summaryText}>
                                        <Markdown>{organization.businessSummary}</Markdown>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Spinner animation + markdown styles */}
                <style>{`
                    @keyframes heroCardSpin {
                        to { transform: rotate(360deg); }
                    }
                    @keyframes researchTourPulse {
                        0%, 100% {
                            box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.3), 0 0 30px rgba(251, 191, 36, 0.4);
                        }
                        50% {
                            box-shadow: 0 0 0 8px rgba(251, 191, 36, 0.2), 0 0 50px rgba(251, 191, 36, 0.6);
                        }
                    }
                    @keyframes researchTourBounce {
                        0%, 100% {
                            transform: translateY(-50%) translateX(0);
                        }
                        50% {
                            transform: translateY(-50%) translateX(-6px);
                        }
                    }
                    .business-summary-markdown h3 {
                        font-size: 14px;
                        font-weight: 700;
                        color: #1f2937;
                        margin: 12px 0 4px 0;
                    }
                    .business-summary-markdown h3:first-child {
                        margin-top: 0;
                    }
                    .business-summary-markdown p {
                        margin: 4px 0;
                    }
                    .business-summary-markdown ul, .business-summary-markdown ol {
                        margin: 4px 0;
                        padding-left: 20px;
                    }
                    .business-summary-markdown li {
                        margin: 2px 0;
                    }
                    .business-summary-markdown strong {
                        color: #111827;
                    }
                `}</style>
            </div>

            {/* Right Column - Stats & Actions */}
            <div className="org-hero-right" style={styles.rightColumn}>
                <div style={styles.sectionTitle}>Stats</div>
                <div className="org-hero-stats" style={styles.statsContainer}>
                    <div
                        style={styles.statCard}
                        onClick={() => navigate('/ads')}
                    >
                        <p style={styles.statNumber}>{adsCount}</p>
                        <p style={styles.statLabel}>Ads</p>
                    </div>
                    <div
                        style={styles.statCard}
                        onClick={() => navigate('/campaigns')}
                    >
                        <p style={styles.statNumber}>{campaignsCount}</p>
                        <p style={styles.statLabel}>Campaigns</p>
                    </div>
                </div>

                <div style={styles.sectionTitle}>Actions</div>
                <div className="org-hero-actions" style={styles.actionsContainer}>
                    <button
                        style={{ ...styles.actionButton, ...styles.primaryAction }}
                        onClick={() => navigate('/ads')}
                        type="button"
                    >
                        <PlusCircleIcon /> Create Ad
                    </button>
                    <button
                        style={{ ...styles.actionButton, ...styles.secondaryAction }}
                        onClick={() => navigate('/campaigns')}
                        type="button"
                    >
                        <PlusCircleIcon /> Create Campaign
                    </button>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={styles.confirmOverlay} onClick={handleCancelDelete}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>Delete Organization</h3>
                        <p style={styles.confirmText}>
                            Are you sure you want to delete "{organization.organizationName}"? This action cannot be undone.
                        </p>
                        <div style={styles.confirmButtons}>
                            <button style={styles.cancelButton} onClick={handleCancelDelete}>
                                Cancel
                            </button>
                            <button style={styles.deleteButton} onClick={handleConfirmDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle Business Summary Confirmation Modal */}
            {showSummaryToggleConfirm && (
                <div style={styles.confirmOverlay} onClick={() => setShowSummaryToggleConfirm(false)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>
                            {isSummaryEnabled ? 'Disable Business Summary?' : 'Enable Business Summary?'}
                        </h3>
                        <p style={styles.confirmText}>
                            {isSummaryEnabled
                                ? 'When disabled, AI-generated ads will use only your organization name instead of the full business profile. You can re-enable this at any time.'
                                : 'When enabled, AI-generated ads will use your full business profile for more targeted and relevant ad content.'}
                        </p>
                        <div style={styles.confirmButtons}>
                            <button style={styles.cancelButton} onClick={() => setShowSummaryToggleConfirm(false)}>
                                Cancel
                            </button>
                            <button
                                style={{ ...styles.primaryConfirmButton, opacity: isTogglingBs ? 0.7 : 1 }}
                                onClick={handleConfirmToggleSummary}
                                disabled={isTogglingBs}
                            >
                                {isTogglingBs ? 'Updating...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { OrganizationHeroCard };
